import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiCallError, api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

type Status = 'verifying' | 'error';

export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // React 18 StrictMode (and any future double-render) would otherwise fire
  // the effect twice, consume the single-use token on the first call, and
  // then surface a 401 from the second. Guard at start-of-effect so only
  // one network request is ever issued per token per mount.
  const attemptedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('Missing token in URL.');
      return;
    }
    if (attemptedTokenRef.current === token) return;
    attemptedTokenRef.current = token;

    (async () => {
      try {
        const result = await api.verifyMagicLink(token);
        await setSession(result.sessionToken);
        navigate('/', { replace: true });
      } catch (err) {
        setStatus('error');
        if (err instanceof ApiCallError && err.status === 401) {
          setErrorMessage('That sign-in link is invalid, expired, or already used.');
        } else {
          setErrorMessage('Something went wrong. Please request a new link.');
        }
      }
    })();
  }, [params, setSession, navigate]);

  return (
    <main className="min-h-screen bg-page text-fg">
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-start justify-center px-6 py-12">
        {status === 'verifying' ? (
          <>
            <h1 className="mb-2 text-2xl font-medium text-fg">Signing you in…</h1>
            <p className="text-sm text-fg-secondary">One moment.</p>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-2xl font-medium text-fg">Sign-in failed</h1>
            <p className="mb-6 text-sm text-fg-secondary">{errorMessage}</p>
            <Link to="/login" className="text-sm text-accent hover:underline">
              Request a new link
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
